import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import { Company, CompanyTableProps, DepartmentContact } from '../../types/table/company.types';

interface EnhancedCompanyTableProps extends CompanyTableProps {
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
}

import BaseDataTable from '@/components/table/base-data-table';
import { ColumnConfig } from '@/components/types/types';
import { BUSINESS_PARTNER_LABELS, CUSTOMER_SUPPLIER_LABELS } from '@/lib/constants';
import { convertDateTimeFormat } from '@/utils/date-time';
import { getCellStyle } from '@/components/styles/table-styles';

const renderCell = (title: string = '') => (
  <Box sx={getCellStyle('100%')} title={title}>
    {title}
  </Box>
);

const renderDateCell = (value?: string) => {
  const formatted = value ? convertDateTimeFormat(value) : '';
  return renderCell(formatted);
};

const mapPartnerClassification = (partnerClassification?: number): string =>
  partnerClassification !== undefined && partnerClassification !== null
    ? (BUSINESS_PARTNER_LABELS[partnerClassification as keyof typeof BUSINESS_PARTNER_LABELS] ?? '')
    : '';

const mapCustomerCategory = (customerCategory?: number | null): string =>
  customerCategory === null || customerCategory === undefined
    ? ''
    : (CUSTOMER_SUPPLIER_LABELS[customerCategory as keyof typeof CUSTOMER_SUPPLIER_LABELS] ?? '');

const formatSaleStaffNames = (departmentContactLists?: DepartmentContact[]): string => {
  if (!Array.isArray(departmentContactLists) || departmentContactLists.length === 0) return '';
  const names = departmentContactLists
    .map((contact) => {
      const lastName = contact?.saleStaff?.lastName;
      const firstName = contact?.saleStaff?.firstName;
      return lastName && firstName ? `${lastName}${firstName}` : '';
    })
    .filter(Boolean);
  if (names.length === 0) return '';
  return names.join(' / ');
};

const CompanyTable: React.FC<EnhancedCompanyTableProps> = ({
  onCompanyClick,
  data = [],
  loading = false,
  currentPage = 1,
  itemsPerPage = 100,
  totalItems = 0,
  onPageChange,
  onItemsPerPageChange,
  sortField = 'updatedAt',
  sortOrder = 'desc',
  onSortChange,
}) => {
  const columns: ColumnConfig<Company>[] = useMemo(
    () => [
      {
        key: 'partnerClassification',
        label: '会社区分',
        sortable: false,
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: number) => renderCell(mapPartnerClassification(value)),
      },
      {
        key: 'formalName',
        label: '正式名称',
        sortable: false,
        width: { xs: '20%', sm: '20%', md: '20%' },
        render: (value: string) => renderCell(value || ''),
      },
      {
        key: 'abbreviationName',
        label: '略称',
        sortable: false,
        width: { xs: '15%', sm: '15%', md: '15%' },
        render: (value: string) => renderCell(value || ''),
      },
      {
        key: 'departmentContacts',
        label: '営業',
        sortable: false,
        width: { xs: '20%', sm: '20%', md: '20%' },
        render: (value: DepartmentContact[]) => renderCell(formatSaleStaffNames(value)),
      },
      {
        key: 'customerCategory',
        label: '得意先/仕入先カテゴリ',
        sortable: false,
        width: { xs: '15%', sm: '15%', md: '15%' },
        render: (value: number | null) => renderCell(mapCustomerCategory(value)),
      },
      {
        key: 'updatedAt',
        label: '更新日時',
        sortable: true,
        sortField: 'updatedAt',
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: string) => renderDateCell(value),
      },
      {
        key: 'createdAt',
        label: '作成日時',
        sortable: true,
        sortField: 'createdAt',
        width: { xs: '10%', sm: '10%', md: '10%' },
        render: (value: string) => renderDateCell(value),
      },
    ],
    []
  );

  return (
    <BaseDataTable<Company>
      data={data}
      loading={loading}
      onRowClick={onCompanyClick}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      sortField={sortField}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      columns={columns}
      enableServerSort={true}
    />
  );
};

export default CompanyTable;
